import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';

const { isImpersonating } = useImpersonation();

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'account.cache_invalidated': this.onCacheInvalidate,
      'account.enrichment_completed': this.onEnrichmentCompleted,
      'copilot.message.created': this.onCopilotMessageCreated,
      // M26.14 Part 5/13 — Live UI Synchronization (NEXCEO). Kanban/All
      // Leads/Verified Leads/Lead Drawer/Calendar patch live instead of only
      // refreshing on next page load.
      'nexceo_lead.updated': this.onNexceoLeadUpdated,
      'nexceo_site_visit.updated': this.onNexceoSiteVisitUpdated,
      // M29.2 Voice Experience — standardized events broadcast by
      // Nexceo::Voice::Broadcaster (app/services/nexceo/voice/broadcaster.rb)
      // over this same account_# stream. Thin pass-through to the `emitter`
      // bus, matching every other cross-cutting event in this file — the
      // Nexceo Voice Pinia store (stores/nexceoVoice.js) is subscribed to
      // from NexceoVoiceListener.vue, not from here, since Pinia stores
      // aren't reliably reachable from this plain (non-component) class.
      'voice.started': this.onVoiceStarted,
      'voice.ringing': this.onVoiceRinging,
      'voice.accepted': this.onVoiceAccepted,
      'voice.connected': this.onVoiceConnected,
      'voice.ended': this.onVoiceEnded,
      'voice.failed': this.onVoiceFailed,
      'voice.missed': this.onVoiceMissed,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.fetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.fetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const {
      conversation: { last_activity_at: lastActivityAt },
      conversation_id: conversationId,
    } = data;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    this.app.$store.dispatch('updateConversationLastActivity', {
      lastActivityAt,
      conversationId,
    });
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.fetchConversationStats();
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  onEnrichmentCompleted = () => {
    this.app.$store.dispatch('accounts/get', { silent: true });
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  // M26.14 Part 5/13 — reuses the existing nexceoLeads/nexceoSiteVisits show
  // endpoints + UPDATE_LEAD/UPDATE_SITE_VISIT mutations (see
  // store/modules/nexceo/leads.js and siteVisits.js) — no new payload
  // contract, just wiring the existing fetch-and-patch actions to the event.
  onNexceoLeadUpdated = data => {
    this.app.$store.dispatch('nexceoLeads/refreshLead', data.lead_id);
    emitter.emit('nexceo_lead.updated', data);
  };

  // Reschedule-audit fix: a reschedule only ever changes the SiteVisit row,
  // never the Lead (site_visit_status stays 'scheduled' either way), so
  // Nexceo::Lead's own after_commit never dispatches nexceo_lead.updated for
  // it. The Lead's next_site_visit_at/next_site_visit_status are computed at
  // serialize time (not stored columns — see leads_controller#lead_json), so
  // any already-cached Lead object's derived visit time goes stale unless we
  // also refresh the Lead here, reusing the lead_id already in this payload.
  onNexceoSiteVisitUpdated = data => {
    this.app.$store.dispatch(
      'nexceoSiteVisits/refreshSiteVisit',
      data.site_visit_id
    );
    if (data.lead_id) {
      this.app.$store.dispatch('nexceoLeads/refreshLead', data.lead_id);
    }
    emitter.emit('nexceo_site_visit.updated', data);
  };

  // M29.2 Voice Experience — one handler per standardized event rather than
  // a single generic one, because BaseActionCableConnector's dispatcher
  // (`this.events[event](data)`) only ever passes the payload, never the
  // event name — see app/javascript/shared/helpers/BaseActionCableConnector.js.
  // eslint-disable-next-line class-methods-use-this
  onVoiceStarted = data => emitter.emit('voice.started', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceRinging = data => emitter.emit('voice.ringing', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceAccepted = data => emitter.emit('voice.accepted', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceConnected = data => emitter.emit('voice.connected', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceEnded = data => emitter.emit('voice.ended', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceFailed = data => emitter.emit('voice.failed', data);

  // eslint-disable-next-line class-methods-use-this
  onVoiceMissed = data => emitter.emit('voice.missed', data);
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
